import { AppActions, AppState, IUserData, actionTypes, AtaSettings } from '../Types';
import { find } from 'lodash';
import logger from 'Utils/Logger';
import {
  normalizeScoreTheme,
  readScoreThemePreference,
  scoreThemeToPracticeDarkMode,
  writeScoreThemePreference,
} from 'Utils/ScoreTheme';
import { DEFAULT_FINGERING_DISPLAY } from 'Fingering/display';

export const defaultAtaSettings: AtaSettings = {
  ataEnabled: true,
  //goalTempo: 150,
  goalTemposByTier: {},
  usingCustomGoalTempo: false,
  usingCustomSettings: false,
  customDecAmt: 10,
  customIncAmt: 5,
  customDecThreshold: 80,
  customIncThreshold: 95,
};

/** The unit the roadmap opens on before the user picks another. */
export const DEFAULT_UNIT_SELECT = 1;

export const defaultState: AppState = {
  errors: [],
  loading: false,
  data: [],
  unitSelect: DEFAULT_UNIT_SELECT,
  levelSelect: 0,
  tempo: 72,
  tutorialData: [],
  tutorialDataProcessing: false,
  userLevelData: [],
  levelData: [],
  tiersByLevels: [],
  midiInput: null,
  lastShownSurvey: null,
  isMidiConnected: null,
  hasShownIntroduction: null,
  userDataFetched: false,
  userData: {
    // set some defaults so the reducer senses the change
    last_shown_survey: null,
    lesson_tutorial_seenat: null,
    repertoire_tutorial_seenat: null,
    practice_score_theme: readScoreThemePreference() ?? 'light',
    practice_dark_mode: scoreThemeToPracticeDarkMode(readScoreThemePreference() ?? 'light'),
    sight_reading_transposition: 0,
  },
  isFullScreen: false,
  repPageSelected: 'Home',
  errorRecognitionActive: { left: true, right: true },
  playOnStart: false,
  interactedWithPage: false,
  showLevelPreview: false,
  pendingDeletion: false,
  dashboardCacheInvalidated: false,
  dashboardData: null,
  zoomLevel: 0.9,
  tempoValues: {},
  ataSettings: defaultAtaSettings,
  unseenApptourFeatures: [],
  sightReadingCursorVisible: true,
  sightReadingFingeringDisplay: DEFAULT_FINGERING_DISPLAY,
};

const getStateWithLocalPracticePreference = (): AppState => ({
  ...defaultState,
  userData: {
    ...defaultState.userData,
    practice_score_theme: readScoreThemePreference() ?? 'light',
    practice_dark_mode: scoreThemeToPracticeDarkMode(readScoreThemePreference() ?? 'light'),
  },
});

export const mainAppReducer = (state = defaultState, action: AppActions): AppState => {
  switch (action.type) {
    case actionTypes.CLEAR_DATA:
      logger.debug('mainAppReducer: CLEAR_DATA action received, resetting state to default');
      return getStateWithLocalPracticePreference();
    case actionTypes.FETCH_CLIENT_DATA_REQUEST:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.FETCH_CLIENT_DATA_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [action.payload.data],
        errors: [],
      };

    case actionTypes.FETCH_CLIENT_DATA_ERROR:
      return {
        ...state,
        loading: false,
        errors: [action.payload.error],
      };

    case actionTypes.SET_LEVEL_SELECT:
      return {
        ...state,
        levelSelect: action.payload.levelSelect,
        currentUserLevelData: find(
          state.userLevelData,
          (uld) =>
            uld.level?.level_number === state.levelData[action.payload.levelSelect]?.level_number,
        ),
        currentLevelTiers: find(
          state.tiersByLevels,
          (tbl) => tbl.level_number === state.levelData[action.payload.levelSelect]?.level_number,
        ),
      };

    case actionTypes.SET_UNIT_SELECT:
      return {
        ...state,
        unitSelect: action.payload.unitSelect,
      };

    case actionTypes.FETCH_TUTORIAL_DATA_SUCCESS:
      return {
        ...state,
        tutorialData: action.payload.tutorialData,
      };

    case actionTypes.FETCH_TUTORIAL_DATA_PROCESSING:
      return {
        ...state,
        tutorialDataProcessing: action.payload,
      };

    // Resets `userDataFetched` so the loader gate closes for this boot. This is
    // load-bearing: `mainAppPersistConfig` has no blacklist and reconciles with
    // hardSet, so the flag rehydrates `true` from the previous session and would
    // otherwise let the app render against stale data (cf. MUS-264, where a stale
    // persisted flag deadlocked startup).
    //
    // Safe against the duplicate AUTHENTICATED_STARTUP that MUS-402 is about,
    // because `onUserDataRequest` dispatches SET_USER_DATA as the LAST step of the
    // startup chain: a duplicate arriving mid-run is followed by that dispatch, and
    // one arriving after the run completes starts a fresh run (takeLeading's slot is
    // free by then) which ends the same way.
    case actionTypes.AUTHENTICATED_STARTUP:
      return {
        ...state,
        userDataFetched: false,
        userDataLoadedThisRun: false,
      };

    case actionTypes.FETCH_USER_LEVEL_SUCCESS:
      return {
        ...state,
        userLevelData: action.payload.userLevelData,
      };

    case actionTypes.FETCH_LEVEL_DATA_SUCCESS:
      return {
        ...state,
        levelData: action.payload.levelData,
      };

    case actionTypes.UPDATE_TIERS_BY_LEVELS:
      return {
        ...state,
        tiersByLevels: action.payload,
        tiersByLevelsLoadedThisRun: true,
      };

    case actionTypes.MIDI_INPUT_SETUP:
      return {
        ...state,
        midiInput: action.payload,
      };

    case actionTypes.SET_SHOWN_INTRODUCTION:
      return {
        ...state,
        hasShownIntroduction: action.payload,
      };

    case actionTypes.SET_USER_DATA: {
      const storedScoreTheme = readScoreThemePreference();
      const scoreTheme =
        storedScoreTheme ??
        normalizeScoreTheme(
          action.payload.practice_score_theme ?? state.userData?.practice_score_theme,
          action.payload.practice_dark_mode ?? state.userData?.practice_dark_mode,
        );
      if (storedScoreTheme === undefined) {
        writeScoreThemePreference(scoreTheme);
      }
      return {
        ...state,
        userDataFetched: true,
        // Only the backend's own answer counts as loaded by this run: a record
        // the app rebuilt (a purchase refresh, the welcome screen) may be a
        // restored copy.
        userDataLoadedThisRun: action.meta?.fromBackend === true || state.userDataLoadedThisRun,
        userData: {
          ...action.payload,
          prefer_audio_recognition_input: action.payload.prefer_audio_recognition_input ?? false,
          practice_score_theme: scoreTheme,
          practice_dark_mode: scoreThemeToPracticeDarkMode(scoreTheme),
          // Set defaults for metronome and downbeats if undefined or null
          rep_metronome_sound: action.payload.rep_metronome_sound ?? true,
          rep_downbeats_sound: action.payload.rep_downbeats_sound ?? true,
          // Concert pitch (0) when the server hasn't stored a preference yet.
          sight_reading_transposition: action.payload.sight_reading_transposition ?? 0,
        },
      };
    }

    case actionTypes.UPDATE_USER_DATA:
      if (
        action.payload.practice_score_theme !== undefined ||
        typeof action.payload.practice_dark_mode === 'boolean'
      ) {
        writeScoreThemePreference(
          normalizeScoreTheme(
            action.payload.practice_score_theme,
            action.payload.practice_dark_mode,
          ),
        );
      }
      // this code checks for actual changes and otherwise returns state. (Prevents rerender on useEffect dependency)
      // way way more verbose because of type guard for keys
      if (state.userData) {
        logger.debug('Updating user data');
        let changesPresent = false;
        const isKeyOfIUserData = (key: keyof IUserData): boolean => {
          if (!state.userData) return false;
          return key in state.userData;
        };
        for (const key in action.payload) {
          const keyWithType: keyof IUserData = key as keyof IUserData; // WTF typescript...
          if (
            isKeyOfIUserData(keyWithType) &&
            state.userData[keyWithType] !== action.payload[keyWithType]
          ) {
            changesPresent = true;
            break;
          }
        }

        if (!changesPresent) {
          return state;
        }
      }
      return {
        ...state,
        userData: {
          ...state.userData,
          ...action.payload,
        },
      };

    case actionTypes.APPLY_PLAY_INPUT_ANSWER:
      if (!state.userData) return state;
      return {
        ...state,
        userData: {
          ...state.userData,
          ...action.payload,
        },
      };

    case actionTypes.LOAD_USER_STATE:
      return {
        ...state,
        ...action.payload,
      };

    case actionTypes.SET_FULL_SCREEN:
      return {
        ...state,
        isFullScreen: action.payload,
      };

    case actionTypes.SET_SUBSCRIPTION_STATUS:
      // Return state if subscription_status is not different; if returns spread of userData WILL trigger useEffect on userData
      if (!state.userData || state.userData.subscription_status == action.payload) {
        return state;
      }
      return {
        ...state,
        userData: {
          ...state.userData,
          subscription_status: action.payload,
          // This refresh brings the status alone. The time on record belongs to the status it
          // replaces, so it is dropped rather than left to describe the new one (MUS-598).
          subscription_status_changed_at: null,
        },
      };

    case actionTypes.SET_REP_PAGE_SELECTED:
      return {
        ...state,
        repPageSelected: action.payload,
      };

    case actionTypes.SET_ERROR_RECOGNITION_ACTIVE:
      return {
        ...state,
        errorRecognitionActive: action.payload,
      };

    case actionTypes.SET_PLAY_ON_START:
      return {
        ...state,
        playOnStart: action.payload.playOnStart,
      };

    case actionTypes.SET_INTERACTED_WITH_PAGE:
      // Dispatched on every mousedown (see App.tsx autoplay handler). Bail out
      // when the value is unchanged so we don't produce a new state object on
      // each click — that needless churn re-renders subscribers every click and,
      // e.g., makes the app-tour Popper recompute and jump a few px.
      if (state.interactedWithPage === action.payload) return state;
      return {
        ...state,
        interactedWithPage: action.payload,
      };

    case actionTypes.INVALIDATE_DASHBOARD_CACHE:
      return {
        ...state,
        dashboardCacheInvalidated: !state.dashboardCacheInvalidated,
        dashboardData: null, // Clear cached data when invalidating
      };

    case actionTypes.SET_DASHBOARD_DATA: {
      console.log('SET_DASHBOARD_DATA reducer called with payload:', action.payload);
      console.log('Previous state dashboardData:', state.dashboardData);
      const newState = {
        ...state,
        dashboardData: action.payload,
      };
      console.log('New state dashboardData:', newState.dashboardData);
      return newState;
    }

    case actionTypes.SET_ZOOM_LEVEL:
      return {
        ...state,
        zoomLevel: action.payload ?? state.zoomLevel,
      };

    case actionTypes.SET_TEMPO: {
      const { tempo, level, tier } = action.payload;
      const tempoValues = state?.tempoValues ?? {};
      const levelTempoValues = tempoValues[level] ?? {};
      const newTempoValues = {
        ...tempoValues,
        [level]: {
          ...levelTempoValues,
          [tier]: tempo,
        },
      };
      /*const newSettings = {
        ...settings,
        [action.payload.tier]: {
          ...(settings[action.payload.tier] ?? defaultTempoSettings),
          currentTempo: action.payload.tempo,
        },
      };*/
      return {
        ...state,
        tempoValues: newTempoValues,
      };
    }

    case actionTypes.SET_AUTO_TEMPO_ADJUSTMENT: {
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        ataEnabled: action.payload,
      };
      return {
        ...state,
        ataSettings,
      };
    }

    case actionTypes.SET_CUSTOM_GOAL_TEMPO: {
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        goalTemposByTier: {
          ...(state?.ataSettings?.goalTemposByTier ?? {}),
          [action.payload.tier]: action.payload.tempo,
        },
      };
      return {
        ...state,
        ataSettings,
      };
    }

    case actionTypes.SET_USING_CUSTOM_GOAL_TEMPO: {
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        usingCustomGoalTempo: action.payload,
      };
      return {
        ...state,
        ataSettings,
      };
    }

    case actionTypes.SET_UNSEEN_APPTOUR_FEATURES: {
      return {
        ...state,
        unseenApptourFeatures: action.payload,
      };
    }

    case actionTypes.SET_SIGHT_READING_CURSOR_VISIBLE:
      return {
        ...state,
        sightReadingCursorVisible: action.payload,
      };

    case actionTypes.SET_SIGHT_READING_FINGERING_DISPLAY:
      return {
        ...state,
        sightReadingFingeringDisplay: action.payload,
      };

    case actionTypes.SET_CUSTOM_ATA_SETTINGS: {
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        ...action.payload,
      };
      return {
        ...state,
        ataSettings,
      };
    }

    case actionTypes.LOAD_ATA_SETTINGS: {
      // Merge server-hydrated custom ATA settings on top of defaults + whatever
      // is already in state. Same shape as SET_CUSTOM_ATA_SETTINGS but this
      // action is not persisted back (see AppData saga).
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        ...action.payload,
      };
      return {
        ...state,
        ataSettings,
      };
    }

    default:
      // console.log("mainAppReducer: unhandled action type:", action.type);
      return state;
  }
};
